import "./Home.css";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GetNews } from "../../apis/api/NewsApi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { DashboardApi, RecentMember } from "../../apis/api/DashboardApi";
import { ProfileGet } from "../../apis/api/ProfileApi";
import { formatDMY } from "../../utils/date";
import Cookies from "js-cookie";
import Lodderr from "../../components/Lodder/Lodder";
import GeoChart from "./WorldMap";

const fadeUp   = { hidden:{opacity:0,y:20},  show:{opacity:1,y:0} };
const fadeLeft = { hidden:{opacity:0,x:-16}, show:{opacity:1,x:0} };
const stagger  = { show:{ transition:{ staggerChildren:.08 } } };
const scaleIn  = { hidden:{opacity:0,scale:.94}, show:{opacity:1,scale:1} };

const pad = (n) => String(n ?? 0).padStart(2, "0");
const split = (ms) => ({
  days:    Math.floor(ms / 86400000),
  hours:   Math.floor((ms % 86400000) / 3600000),
  minutes: Math.floor((ms % 3600000)  / 60000),
  seconds: Math.floor((ms % 60000)    / 1000),
});
const fmtDate = (ts) => {
  if (!ts) return "—";
  return new Date(Number(ts) * 1000)
    .toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })
    .replace(/ /g, "-");
};

export default function Dashboard() {
  const [timeLeft, setTimeLeft] = useState(null);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const location  = useLocation();

  const company_id = Cookies.get("company_id");
  const user_id    = Cookies.get("userID");
  const user       = JSON.parse(Cookies.get("user") || "{}");

  const { dashboardData, Recentmember, loading } = useSelector((s) => s.DashboardSlice);
  const { news } = useSelector((s) => s.NewsSlice);
  const profileData = useSelector((s) => s.ProfileSlice?.profileData);

  useEffect(() => {
    dispatch(RecentMember());
    dispatch(GetNews());
    dispatch(DashboardApi({ company_id, user_id }));
    dispatch(ProfileGet({ userId: user_id, company_id }));
  }, []);

  useEffect(() => {
    if (!dashboardData?.membership_start || !dashboardData?.membership_expire) {
      setTimeLeft({});
      return;
    }
    const start  = Number(dashboardData.membership_start)  * 1000;
    const expiry = Number(dashboardData.membership_expire) * 1000;
    let id;
    const tick = () => {
      const now = Date.now();
      if (now < start)        setTimeLeft({ status:"not_started", ...split(start - now) });
      else if (now >= expiry) { setTimeLeft({ status:"expired" }); clearInterval(id); }
      else                    setTimeLeft({ status:"active", ...split(expiry - now) });
    };
    tick();
    id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dashboardData?.membership_start, dashboardData?.membership_expire]);

  const go = (r) => r && navigate(r, { state: { from: location.pathname } });

  /* membership progress % */
  const membershipPct = (() => {
    const s = Number(dashboardData?.membership_start)  * 1000;
    const e = Number(dashboardData?.membership_expire) * 1000;
    if (!s || !e) return 0;
    const now = Date.now();
    const pct = Math.round(((now - s) / (e - s)) * 100);
    return Math.min(Math.max(pct, 0), 100);
  })();

  const stripItems = [
    { icon:"bx bx-buildings",  label:"Total Companies",  val: dashboardData?.total_registrations ?? 0, route:"/user" },
    { icon:"bx bx-user-check", label:"Active Members",   val: dashboardData?.active_members      ?? 0, route:"/activeMember" },
    { icon:"bx bx-globe",      label:`Members in ${dashboardData?.country||"Country"}`, val: dashboardData?.country_members ?? 0, route:"/memberinCountry" },
    { icon:"bx bx-id-card",    label: dashboardData?.plan_name?.split(":")[1] || "Current Plan",
      val: `${fmtDate(dashboardData?.membership_start)} – ${fmtDate(dashboardData?.membership_expire)}`,
      isText:true, route:null },
  ];

  const timerUnits = [
    { val:timeLeft?.days,    lbl:"Days" },
    { val:timeLeft?.hours,   lbl:"Hours" },
    { val:timeLeft?.minutes, lbl:"Mins" },
    { val:timeLeft?.seconds, lbl:"Secs" },
  ];

  const quickActions = [
    { icon:"bx bx-plus-circle",   label:"New Freight Request", route:"/new_request",      color:"#586AD9" },
    { icon:"bx bx-transfer",      label:"Make Transaction",    route:"/Business_impact",  color:"#0ea5e9" },
    { icon:"bx bx-news",          label:"Read News",           route:"/news",             color:"#10b981" },
    { icon:"bx bx-calendar-event",label:"View Events",         route:"/EventsMenu",       color:"#f59e0b" },
    { icon:"bx bx-chat",          label:"Open Chat",           route:"/chat",             color:"#8b5cf6" },
    { icon:"bx bx-user-circle",   label:"My Profile",          route:"/profile",          color:"#ec4899" },
    { icon:"bx bx-receipt",       label:"My Invoices",         route:"/invoicelist",      color:"#14b8a6" },
    { icon:"bx bx-time-five",     label:"Timeline",            route:"/Timeline",         color:"#f97316" },
  ];

  const freightItems = [
    { label:"Sent Requests",     icon:"bx bx-send",         route:"/sent_request",     bg:"#eef0fb", color:"#586AD9", badge:"View", badgeBg:"#eef0fb", badgeColor:"#586AD9" },
    { label:"Received Requests", icon:"bx bx-inbox",        route:"/receive_request",  bg:"#f0fdf4", color:"#16a34a", badge:"View", badgeBg:"#f0fdf4", badgeColor:"#16a34a" },
    { label:"Accepted Requests", icon:"bx bx-check-circle", route:"/accept_request",   bg:"#fef9c3", color:"#ca8a04", badge:"View", badgeBg:"#fef9c3", badgeColor:"#ca8a04" },
    { label:"New Request",       icon:"bx bx-plus-circle",  route:"/new_request",      bg:"#fce7f3", color:"#db2777", badge:"Create", badgeBg:"#fce7f3", badgeColor:"#db2777" },
  ];

  /* profile completion steps — use live ProfileSlice data */
  const cd = profileData?.data?.company_data;
  const profileSteps = [
    { label:"Company Logo",   done: !!(cd?.company_logo   && cd.company_logo   !== "null" && String(cd.company_logo).trim()   !== ""), icon:"bx bx-image",     bg:"#eef0fb", color:"#586AD9" },
    { label:"Company Banner", done: !!(cd?.company_banner && cd.company_banner !== "null" && String(cd.company_banner).trim() !== ""), icon:"bx bx-landscape", bg:"#f0fdf4", color:"#16a34a" },
    { label:"Bio / About",    done: !!(cd?.bio            && String(cd.bio).replace(/<[^>]*>/g,"").trim() !== ""),                      icon:"bx bx-edit",     bg:"#fef9c3", color:"#ca8a04" },
    { label:"Social Media",   done: !!(cd?.facebook_url || cd?.twitter_url || cd?.linkedIn_url || cd?.instagram_url),                  icon:"bx bx-share-alt",bg:"#fce7f3", color:"#db2777" },
  ];
  const completedSteps = profileSteps.filter((s) => s.done).length;
  const profilePct = Math.round((completedSteps / profileSteps.length) * 100);

  return (
    <>
      {loading && <Lodderr />}
      <motion.div className="db" variants={stagger} initial="hidden" animate="show">

        {/* ── WELCOME BANNER ── */}
        <motion.div className="db-banner" variants={fadeUp} transition={{ duration:.5, ease:[.22,.68,0,1.2] }}>
          <div className="db-banner-inner">
            <motion.div variants={fadeLeft} transition={{ duration:.45, delay:.1 }}>
              <p className="db-banner-greet">Welcome back 👋</p>
              <h1 className="db-banner-name">{user?.fullname || "Member"}</h1>
              <p className="db-banner-sub">{user?.company_name || "Member Panel"}</p>
            </motion.div>
            <motion.div className="db-banner-actions" variants={fadeLeft} transition={{ duration:.45, delay:.2 }}>
              <button className="db-banner-btn solid" onClick={() => go("/profile")}>
                <i className="bx bx-user"></i> View Profile
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* ── QUICK STATS STRIP ── */}
        <motion.div className="db-strip" variants={fadeUp} transition={{ duration:.45, delay:.1 }}>
          {stripItems.map((s, i) => (
            <motion.div key={i} className="db-strip-item"
              onClick={() => go(s.route)}
              style={{ cursor: s.route ? "pointer" : "default" }}
              whileHover={{ backgroundColor:"#f5f6fe" }}
              transition={{ duration:.15 }}>
              <span className="db-strip-icon"><i className={s.icon}></i></span>
              <div>
                {!s.isText && <div className="db-strip-val">{s.val}</div>}
                {s.isText  && <div className="db-strip-val" style={{ fontSize:".78rem", fontWeight:700 }}>{s.val}</div>}
                <div className="db-strip-lbl">{s.label}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* ── ROW: MEMBERSHIP STATUS + PROFILE COMPLETION ── */}
        <div className="db-two-col">

          {/* Membership countdown */}
          <motion.div className="db-card" style={{ marginBottom:0 }} variants={fadeUp} transition={{ duration:.45, delay:.15 }}>
            <div className="db-card-head">
              <h2 className="db-card-title"><i className="bx bx-time-five"></i> Membership Status</h2>
              {dashboardData?.plan_name && <span className="db-card-meta">{dashboardData.plan_name.split(":")[1]}</span>}
            </div>
            {/* progress bar */}
            <div className="db-plan-body">
              <div className="db-plan-row">
                <span className="db-plan-name">{dashboardData?.plan_name?.split(":")[1] || "Plan"}</span>
                <span className="db-plan-pct">{membershipPct}% used</span>
              </div>
              <div className="db-plan-track">
                <motion.div className="db-plan-fill"
                  initial={{ width:0 }}
                  animate={{ width:`${membershipPct}%` }}
                  transition={{ duration:.8, ease:"easeOut", delay:.3 }} />
              </div>
              <div className="db-plan-dates">
                <span>{fmtDate(dashboardData?.membership_start)}</span>
                <span>{fmtDate(dashboardData?.membership_expire)}</span>
              </div>
            </div>
            <div className="db-countdown" style={{ paddingTop:0 }}>
              <div className="db-cd-left">
                <span className="db-cd-icon"><i className="bx bx-shield-quarter"></i></span>
                <div>
                  <p className="db-cd-title">
                    {timeLeft?.status === "active"      && "Expires in"}
                    {timeLeft?.status === "not_started" && "Starts in"}
                    {timeLeft?.status === "expired"     && "Membership expired"}
                    {(!timeLeft || !timeLeft.status)    && "Loading…"}
                  </p>
                  <p className="db-cd-sub">{fmtDate(dashboardData?.membership_expire)}</p>
                </div>
              </div>
              <AnimatePresence mode="wait">
                {(timeLeft?.status === "active" || timeLeft?.status === "not_started") && (
                  <motion.div className="db-timer" key="timer"
                    initial={{ opacity:0, scale:.9 }} animate={{ opacity:1, scale:1 }}
                    exit={{ opacity:0, scale:.9 }} transition={{ duration:.3 }}>
                    {timerUnits.map((t, i) => (
                      <React.Fragment key={t.lbl}>
                        {i > 0 && <span className="db-tcolon">:</span>}
                        <motion.div className="db-tbox"
                          whileHover={{ y:-4, boxShadow:"0 10px 24px rgba(88,106,217,.22)" }}
                          transition={{ type:"spring", stiffness:400, damping:20 }}>
                          <AnimatePresence mode="popLayout">
                            <motion.span key={t.val} className="db-tnum"
                              initial={{ y:-12, opacity:0 }} animate={{ y:0, opacity:1 }}
                              exit={{ y:12, opacity:0 }} transition={{ duration:.2 }}>
                              {pad(t.val)}
                            </motion.span>
                          </AnimatePresence>
                          <span className="db-tlbl">{t.lbl}</span>
                        </motion.div>
                      </React.Fragment>
                    ))}
                  </motion.div>
                )}
                {timeLeft?.status === "expired" && (
                  <motion.span key="expired" className="db-badge expired"
                    initial={{ opacity:0, scale:.8 }} animate={{ opacity:1, scale:1 }}
                    transition={{ type:"spring", stiffness:300 }}>
                    <i className="bx bx-error-circle"></i> Expired
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Profile completion */}
          <motion.div className="db-card" style={{ marginBottom:0 }} variants={fadeUp} transition={{ duration:.45, delay:.18 }}>
            <div className="db-card-head">
              <h2 className="db-card-title"><i className="bx bx-user-circle"></i> Profile Completion</h2>
              <span className="db-card-meta" style={{ color:"#586AD9", fontWeight:700 }}>{profilePct}%</span>
            </div>
            <div className="db-profile-body">
              <div className="db-plan-track" style={{ marginBottom:"1rem" }}>
                <motion.div className="db-plan-fill"
                  initial={{ width:0 }}
                  animate={{ width:`${profilePct}%` }}
                  transition={{ duration:.8, ease:"easeOut", delay:.3 }} />
              </div>
              <div className="db-profile-steps">
                {profileSteps.map((step, i) => (
                  <motion.div key={step.label} className="db-profile-step"
                    initial={{ opacity:0, x:-10 }}
                    animate={{ opacity:1, x:0 }}
                    transition={{ delay: i * .07 + .2, duration:.28 }}>
                    <span className="db-step-icon" style={{ background: step.bg, color: step.color }}>
                      <i className={step.icon}></i>
                    </span>
                    <span className="db-step-label">{step.label}</span>
                    <span className="db-step-check">
                      {step.done
                        ? <i className="bx bx-check-circle" style={{ color:"#22c55e" }}></i>
                        : <i className="bx bx-circle" style={{ color:"#cbd5e1" }}></i>}
                    </span>
                  </motion.div>
                ))}
              </div>
              {profilePct < 100 && (
                <button className="db-view-btn" style={{ marginTop:".85rem", width:"100%", justifyContent:"center" }}
                  onClick={() => go("/profile")}>
                  Complete Profile <i className="bx bx-right-arrow-alt"></i>
                </button>
              )}
            </div>
          </motion.div>
        </div>

        {/* ── QUICK ACTIONS ── */}
        <motion.div className="db-card" variants={fadeUp} transition={{ duration:.45, delay:.20 }}>
          <div className="db-card-head">
            <h2 className="db-card-title"><i className="bx bx-grid-alt"></i> Quick Actions</h2>
          </div>
          <motion.div className="db-actions-grid" variants={stagger} initial="hidden" animate="show">
            {quickActions.map((a) => (
              <motion.div key={a.route} className="db-action-item"
                variants={scaleIn} transition={{ duration:.3 }}
                whileHover={{ y:-4 }}
                onClick={() => go(a.route)}>
                <span className="db-action-icon" style={{ background:`linear-gradient(135deg,${a.color}cc,${a.color})` }}>
                  <i className={a.icon}></i>
                </span>
                <p className="db-action-label">{a.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* ── FULL NEWS GRID ── */}
        {news?.length > 0 && (
          <motion.div className="db-card" variants={fadeUp} transition={{ duration:.45, delay:.26 }}>
            <div className="db-card-head">
              <h2 className="db-card-title"><i className="bx bx-news"></i> All Latest News</h2>
              <button className="db-view-btn" onClick={() => go("/news")}>
                View All <i className="bx bx-right-arrow-alt"></i>
              </button>
            </div>
            <motion.div className="db-news-grid" variants={stagger} initial="hidden" animate="show">
              {news.slice(0, 4).map((item) => (
                <motion.div className="db-news-item" key={item.id}
                  variants={scaleIn} transition={{ duration:.35, ease:[.22,.68,0,1.2] }}
                  whileHover={{ y:-6, boxShadow:"0 20px 40px rgba(88,106,217,.15)" }}
                  onClick={() => navigate("/newsArticle", { state:{ newsData:item, from:location.pathname } })}>
                  <div className="db-news-img-wrap">
                    <motion.img src={item.image?.replace("/thumb","")} className="db-news-img" alt={item.name}
                      whileHover={{ scale:1.07 }} transition={{ duration:.4 }}
                      onError={(e) => { e.target.style.display="none"; }} />
                    <span className="db-news-tag">News</span>
                  </div>
                  <div className="db-news-body">
                    <span className="db-news-date"><i className="bx bx-calendar"></i>{formatDMY(item.date)}</span>
                    <p className="db-news-name">{item.name}</p>
                    <p className="db-news-desc">{item.title}</p>
                    <div className="db-news-footer">
                      <button className="db-news-btn"><i className="bx bx-book-reader"></i> Read More</button>
                      <motion.span className="db-news-arrow"
                        whileHover={{ x:4, backgroundColor:"#586AD9", color:"#fff" }}
                        transition={{ duration:.15 }}>
                        <i className="bx bx-right-arrow-alt"></i>
                      </motion.span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}

        {/* ── RECENT MEMBERS ── */}
        <motion.div className="db-card" variants={fadeUp} transition={{ duration:.45, delay:.28 }}>
          <div className="db-card-head">
            <h2 className="db-card-title"><i className="bx bx-group"></i> Recent Members</h2>
            <button className="db-view-btn" onClick={() => go("/recentMember")}>
              View All <i className="bx bx-right-arrow-alt"></i>
            </button>
          </div>
          {Array.isArray(Recentmember) && Recentmember.length > 0 ? (
            <motion.div className="db-members-grid" variants={stagger} initial="hidden" animate="show">
              {Recentmember.slice(0, 8).map((m) => (
                <motion.div className="db-member-card" key={m.id}
                  variants={scaleIn} transition={{ duration:.3, ease:[.22,.68,0,1.2] }}
                  whileHover={{ y:-3 }}>
                  <div className="db-member-av-wrap">
                    <img src={m?.company_logo || `${process.env.PUBLIC_URL}/company-logo-design.jpg`}
                      onError={(e) => { e.target.onerror=null; e.target.src=`${process.env.PUBLIC_URL}/company-logo-design.jpg`; }}
                      className="db-member-av" alt={m.company_name} />
                    <span className="db-member-dot"></span>
                  </div>
                  <div className="db-member-info">
                    <p className="db-member-name">{m.company_name}</p>
                    <p className="db-member-country"><i className="bx bx-map-pin"></i>{m.country}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="db-empty"><i className="bx bx-group"></i>No recent members</div>
          )}
        </motion.div>

        {/* ── WORLD MAP ── */}
        <motion.div className="db-card" variants={fadeUp} transition={{ duration:.45, delay:.30 }}>
          <div className="db-card-head">
            <h2 className="db-card-title"><i className="bx bx-world"></i> Members by Country</h2>
          </div>
          <div className="db-map"><GeoChart /></div>
        </motion.div>

      </motion.div>
    </>
  );
}
